import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["neighborDataRadio", "dependentPermissionRadio", "dependentPermissions"]
  static values = { 
    dependentPermissions: { type: Array, default: [] }
  }

  declare dependentPermissionsValue: string[]

  connect() {
    // Initial setup to ensure permissions are consistent
    this.enforcePermissionRules()
    
    // Add event listeners to all radio buttons
    this.element.querySelectorAll('input[type="radio"]').forEach(radio => {
      radio.addEventListener('change', this.handleRadioChange.bind(this))
    })
  }

  // Handle when any radio button changes
  handleRadioChange(event) {
    const radio = event.target as HTMLInputElement
    const name = radio.getAttribute('name')
    const value = radio.value
    
    // Extract the permission key from the name attribute
    const permissionMatch = name?.match(/group\[permissions\]\[(.*?)\]/)
    if (!permissionMatch) return
    
    const permission = permissionMatch[1]
    
    // Special handling for neighbor_data permission
    if (permission === "neighbor_data") {
      if (value === "no_access") {
        // If neighbor_data is set to no_access, set all dependent permissions to no_access
        this.setDependentPermissionsTo("no_access")
      }
    }
    // Special handling for neighbor-dependent permissions
    else if (this.dependentPermissionsValue.includes(permission)) {
      if (value !== "no_access") {
        // If any dependent permission is set to read or higher, ensure neighbor_data has at least read access
        const neighborDataAction = this.getSelectedActionForPermission("neighbor_data")
        if (neighborDataAction === "no_access") {
          this.setPermissionTo("neighbor_data", "read")
        }
      }
    }
  }

  // Main function to enforce permission rules
  enforcePermissionRules() {
    const neighborDataAction = this.getSelectedActionForPermission("neighbor_data")
    
    if (neighborDataAction === "no_access") {
      // If neighbor_data is set to no_access, set all dependent permissions to no_access
      this.setDependentPermissionsTo("no_access")
    } else {
      // Check if any dependent permission has read or higher access
      const hasReadOrHigherAccess = this.checkIfAnyDependentHasReadOrHigher()
      
      // If any dependent has read or higher, ensure neighbor_data has at least read access
      if (hasReadOrHigherAccess && neighborDataAction === "no_access") {
        this.setPermissionTo("neighbor_data", "read")
      }
    }
  }

  // Get the currently selected action for a specific permission
  getSelectedActionForPermission(permission) {
    const selectedRadio = this.element.querySelector(`input[name="group[permissions][${permission}]"]:checked`) as HTMLInputElement | null
    return selectedRadio ? selectedRadio.value : null
  }

  // Set a specific permission to a specific action
  setPermissionTo(permission, action) {
    const radio = this.element.querySelector(`input[name="group[permissions][${permission}]"][value="${action}"]`) as HTMLInputElement | null
    if (radio) {
      radio.checked = true
      // Dispatch a change event to ensure any other listeners are notified
      radio.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  // Set all dependent permissions to a specific action
  setDependentPermissionsTo(action) {
    this.dependentPermissionsValue.forEach(permission => {
      this.setPermissionTo(permission, action)
    })
  }

  // Check if any dependent permission has read or higher access
  checkIfAnyDependentHasReadOrHigher() {
    return this.dependentPermissionsValue.some(permission => {
      const action = this.getSelectedActionForPermission(permission)
      return action === "read" || action === "create" || action === "manage"
    })
  }
} 